/// <reference path="modules.d.ts" />
import './dom-patch';
import 'reflect-metadata';
import React, { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router-dom';
import Content from './pages/index';
import { App, theme } from 'antd';
import './core/assets/site.less';
import './assets/app.less';
import './core/assets/figma.css';
import { AppConfig, initialize as initializeInversify } from './inversify.config';
import 'src/core/utils/linq';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18n from './i18n';
import './core/utils/linq';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/pt';
import 'moment/locale/ru';
import CrashView from './crash-view';
import { ConfigProvider } from 'antd';
import { Theme, themeTokens } from './common/themes';
import { useThemeDetector } from './common/hooks/use-theme-detector';
import EnGB from 'antd/es/locale/en_GB';
import EsEs from 'antd/es/locale/es_ES';
import PtBr from 'antd/es/locale/pt_BR';
import PtPT from 'antd/es/locale/pt_PT';
import FrFr from 'antd/es/locale/fr_FR';
import RuRu from 'antd/es/locale/ru_RU';

import { createSignalRContext } from 'react-signalr/signalr';

export const SignalRContext = createSignalRContext();

//import registerServiceWorker from './registerServiceWorker';

const rootElement = document.getElementById('root');

type ActiveThemeData = {
  selectedTheme: Theme;
  detectSystem: boolean;
};

export const ThemeContext = createContext<{
  themeData: ActiveThemeData;
  setThemeData: Dispatch<SetStateAction<ActiveThemeData>>;
}>({
  setThemeData: () => {},
  themeData: {
    detectSystem: false,
    selectedTheme: Theme.Light,
  },
});

export enum ENV {
  DEV = 'DEV',
  STAGING = 'STAGING',
  PROD = 'PROD',
}

function getLanguage(locale: string) {
  switch (locale) {
    case 'es':
      return EsEs;
    case 'en':
      return EnGB;
    case 'fr':
      return FrFr;
    case 'ru':
      return RuRu;
    case 'pt':
      return PtPT;
    case 'pt-BR':
      return PtBr;
    case 'pt-PT':
      return PtPT;
  }
  return EnGB;
}

const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href')?.split('/')[1];

const ThemedApp = () => {
  const usingLightTemeByDefault = false;

  const [activeThemeData, setActiveThemeData] = useState<ActiveThemeData>({
    detectSystem: !usingLightTemeByDefault,
    selectedTheme: Theme.Light,
  });

  const { i18n } = useTranslation();

  const systemUsesDarkTheme = useThemeDetector();
  const [systemTheme, setSystemTheme] = useState<Theme>(Theme.Light);

  useEffect(() => {
    if (activeThemeData.detectSystem) {
      const theme = systemUsesDarkTheme ? Theme.Dark : Theme.Light;
      setActiveThemeData({
        detectSystem: !usingLightTemeByDefault,
        selectedTheme: usingLightTemeByDefault ? Theme.Light : theme,
      });
      setSystemTheme(theme);
    }
  }, [systemUsesDarkTheme]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ThemeContext.Provider value={{ themeData: activeThemeData, setThemeData: setActiveThemeData }}>
      <ConfigProvider
        locale={getLanguage(i18n.language)}
        theme={{
          hashed: false,
          algorithm: (activeThemeData.selectedTheme ?? systemTheme) === Theme.Dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
          cssVar: true,
          token: themeTokens[activeThemeData.selectedTheme ?? systemTheme],
        }}
      >
        <App style={{ height: '100%' }}>
          <CrashView>
            <Content />
          </CrashView>
        </App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

const router = createBrowserRouter(createRoutesFromElements(<Route path={`*`} element={<ThemedApp />} />), {
  basename: `/${baseUrl}`,
});

fetch(`${process.env.PUBLIC_URL}/api/config`)
  .then((response) => response.json())
  .then((config) => {
    let currentEnv = ENV.PROD;
    if (config.hostUrl.includes('localhost')) currentEnv = ENV.DEV;
    if (config.hostUrl.includes('-dev')) currentEnv = ENV.STAGING;
    config.environment = currentEnv;
    initializeInversify(config);
    ReactDOM.createRoot(rootElement!).render(
      <AppConfig.Provider value={config}>
        <I18nextProvider i18n={i18n}>
          <RouterProvider router={router} />
        </I18nextProvider>
      </AppConfig.Provider>
    );
  });
